import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star } from "lucide-react";

import { audioService } from "@/core/audio/audioService";

/**
 * DoublesFX — Phase 2 special signature for the "doubles" numbers.
 *
 * Values: 11, 22, 33, 44, 55, 66, 77, 88 — any 90-ball number with
 * two identical digits.
 *
 * The signature layers ON TOP of the standard ball reveal without
 * altering the ball itself:
 *   • As soon as the ball opens and the number is unveiled, TWO
 *     golden stars burst out symmetrically (twin motif for "doubles").
 *   • A stylized "DOUBLES ×2" badge fades in beneath the ball.
 *   • The ball area gets a warm secondary glow layer.
 *   • A distinctive two-note twin chime plays via audioService.
 *
 * The revealed number NEVER morphs — this component only decorates.
 *
 * Props:
 *   active   — boolean; when true, the effect plays exactly once and
 *              schedules its own hide after ~1200ms
 */

export const DOUBLES_VALUES = new Set([11, 22, 33, 44, 55, 66, 77, 88]);
export const DOUBLES_DURATION_MS = 1200;

export default function DoublesFX({ active }) {
  // Play the twin chime the moment the effect activates.
  useEffect(() => {
    if (!active) return;
    audioService.playDoubles();
  }, [active]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          data-testid="phase2-doubles-fx"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="absolute inset-0 pointer-events-none flex items-center justify-center"
        >
          {/* Warm secondary halo behind the ball */}
          <motion.div
            aria-hidden
            className="absolute rounded-full"
            style={{
              width: "120%",
              height: "120%",
              background:
                "radial-gradient(circle at 50% 50%, rgba(253, 224, 71, 0.35) 0%, rgba(251, 191, 36, 0.1) 45%, transparent 70%)",
            }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: [0, 1, 0.7, 0], scale: [0.6, 1, 1.05, 1] }}
            transition={{
              duration: DOUBLES_DURATION_MS / 1000,
              times: [0, 0.15, 0.6, 1],
              ease: "easeOut",
            }}
          />

          {/* Twin stars — left */}
          <TwinStar side="left" />
          {/* Twin stars — right */}
          <TwinStar side="right" />

          {/* "DOUBLES ×2" badge */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              delay: 0.12,
              type: "spring",
              stiffness: 300,
              damping: 22,
            }}
            className="absolute"
            style={{
              bottom: "-2.5rem",
              padding: "0.65rem 1.75rem",
              borderRadius: "999px",
              background:
                "linear-gradient(180deg, rgba(253, 224, 71, 0.95) 0%, rgba(245, 158, 11, 0.9) 100%)",
              color: "#1c1917",
              boxShadow:
                "0 0 40px rgba(245, 158, 11, 0.6), 0 8px 24px rgba(0,0,0,0.4)",
              border: "2px solid rgba(255,255,255,0.4)",
            }}
          >
            <div
              data-testid="phase2-doubles-badge"
              className="ic-font-display font-black tracking-[0.35em] flex items-center gap-3"
              style={{ fontSize: "clamp(1rem, 1.8vw, 1.75rem)" }}
            >
              <Star className="w-5 h-5" fill="#1c1917" strokeWidth={0} />
              DOUBLES
              <span
                className="tabular-nums"
                style={{ fontFeatureSettings: "'ss01'" }}
              >
                ×2
              </span>
              <Star className="w-5 h-5" fill="#1c1917" strokeWidth={0} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * TwinStar — a pair of amber stars that burst symmetrically outward,
 * rotate slightly, and fade. `side` picks left/right so both are used
 * together to hint at the "twin" theme.
 */
function TwinStar({ side }) {
  const isLeft = side === "left";
  const sign = isLeft ? -1 : 1;
  return (
    <>
      {/* Big lead star */}
      <motion.div
        aria-hidden
        className="absolute"
        style={{
          top: "40%",
          left: "50%",
          transformOrigin: "center",
          filter:
            "drop-shadow(0 0 18px rgba(253, 224, 71, 0.9)) drop-shadow(0 0 32px rgba(245, 158, 11, 0.5))",
        }}
        initial={{ x: 0, y: 0, opacity: 0, scale: 0.3, rotate: sign * -20 }}
        animate={{
          x: sign * 220,
          y: [-20, -55, -35],
          opacity: [0, 1, 0.9, 0],
          scale: [0.3, 1.4, 1.15, 0.9],
          rotate: sign * 40,
        }}
        transition={{
          duration: DOUBLES_DURATION_MS / 1000,
          ease: "easeOut",
          times: [0, 0.25, 0.65, 1],
        }}
      >
        <Star
          className="w-16 h-16"
          fill="#FDE68A"
          stroke="#F59E0B"
          strokeWidth={1}
        />
      </motion.div>

      {/* Small trailing sparkle */}
      <motion.div
        aria-hidden
        className="absolute"
        style={{
          top: "45%",
          left: "50%",
          filter: "drop-shadow(0 0 10px rgba(253, 224, 71, 0.9))",
        }}
        initial={{ x: 0, y: 0, opacity: 0, scale: 0.2 }}
        animate={{
          x: sign * 140,
          y: 30,
          opacity: [0, 1, 0],
          scale: [0.2, 0.9, 0.4],
        }}
        transition={{
          duration: DOUBLES_DURATION_MS / 1000,
          delay: 0.1,
          ease: "easeOut",
        }}
      >
        <Star
          className="w-8 h-8"
          fill="#FEF3C7"
          stroke="#F59E0B"
          strokeWidth={1}
        />
      </motion.div>
    </>
  );
}
